"use strict";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import TranslateIcon from "@mui/icons-material/Translate";
import { ToastContainer, toast } from "react-toastify";

import {
  GET_USERS,
  CREATE_PROJECT_NEW,
} from "../ApiEndpoints";
import { languages } from "../Data/languages.js";
import {
  VOICE_ENGINE_OPTIONS,
  getSupportedVoiceEngine,
  getVoiceEngineLabel,
} from "../Data/voiceEngines.js";
import {
  formatInputs,
  getLanguageName,
  getRoleOptionLabel,
  getTaskName,
} from "../utils/generalFunctions.js";
import ProjectSearch from "../Components/ProjectSearch";
import UserSelectAutocomplete from "../Components/UserSelectAutoComplete/index.js";

const PROJECT_SETUP_STORAGE_PREFIX = "pendingProjectSetup:";

const createProjectSetupId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getProjectSetupStorageKey = (setupId) =>
  `${PROJECT_SETUP_STORAGE_PREFIX}${setupId}`;

const readProjectSetupFromSession = (setupId) => {
  if (!setupId) {
    return null;
  }

  try {
    const savedSetup = sessionStorage.getItem(getProjectSetupStorageKey(setupId));
    return savedSetup ? JSON.parse(savedSetup) : null;
  } catch (error) {
    console.error("Failed to read saved project setup:", error);
    return null;
  }
};

const buildProjectCreationSearch = ({ setupId, mode, resumeExistingProject }) => {
  const params = new URLSearchParams();

  if (setupId) {
    params.set("setupId", setupId);
  }

  if (mode) {
    params.set("mode", mode);
  }

  if (resumeExistingProject) {
    params.set("resumeExistingProject", "1");
  }

  const search = params.toString();
  return search ? `?${search}` : "";
};

const projectTypeOptions = [
  {
    value: "video",
    title: "Video Dubbing",
    description: "Create a dubbing project with video, source audio, SRT, and lip-sync options.",
    icon: MovieCreationIcon,
  },
  {
    value: "audiobook",
    title: "Audiobook",
    description: "Create a text-to-speech project using one selected voice and target languages.",
    icon: MenuBookIcon,
  },
];

const roleOptions = [
  "transcriber",
  "translator",
  "performancedirection",
  "speakerfaceidentification",
  "lipqualityassurance",
  "qualityassurance",
];

const pageSx = {
  minHeight: "calc(100vh - 90px)",
  width: "100%",
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
  fontFamily: "var(--font-body)",
  background:
    "radial-gradient(circle at 20% 8%, rgba(0, 149, 199, 0.18), transparent 26%), radial-gradient(circle at 88% 28%, rgba(47, 188, 179, 0.16), transparent 28%), linear-gradient(135deg, #eaf7fa 0%, #e6f3ef 48%, #f7fbfc 100%)",
  backgroundAttachment: "fixed",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    opacity: 0.45,
    backgroundImage:
      "linear-gradient(45deg, rgba(255,255,255,0.35) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.35) 75%, transparent 75%, transparent)",
    backgroundSize: "12px 12px",
  },
};

const shellSx = {
  width: "100%",
  maxWidth: "1440px",
  mx: "auto",
  position: "relative",
  zIndex: 1,
};

const glassCardSx = {
  borderRadius: "32px",
  border: "1px solid rgba(255,255,255,0.72)",
  background: "rgba(255,255,255,0.88)",
  boxShadow: "0 24px 80px rgba(13, 57, 78, 0.14)",
  backdropFilter: "blur(18px)",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "18px",
    backgroundColor: "rgba(255,255,255,0.82)",
    transition: "all 180ms ease",
    "& fieldset": {
      borderColor: "rgba(8, 72, 96, 0.16)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 120, 160, 0.45)",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(0, 124, 166, 0.10)",
    },
  },
};

const primaryButtonSx = {
  minHeight: 48,
  px: 3,
  borderRadius: "16px",
  fontWeight: 800,
  textTransform: "none",
  background: "linear-gradient(135deg, #00779f 0%, #00a4c7 100%)",
  boxShadow: "0 14px 28px rgba(0, 119, 159, 0.24)",
  "&:hover": {
    background: "linear-gradient(135deg, #00698d 0%, #0094b5 100%)",
    boxShadow: "0 18px 34px rgba(0, 119, 159, 0.28)",
  },
};

const SectionCard = ({ icon: Icon, title, eyebrow, children, action }) => (
  <Paper elevation={0} sx={{ ...glassCardSx, p: { xs: 2.5, md: 3.5 } }}>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={2}
      mb={3}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {Icon && (
          <Avatar
            sx={{
              width: 42,
              height: 42,
              color: "#00779f",
              bgcolor: "rgba(0, 119, 159, 0.10)",
              borderRadius: "14px",
            }}
          >
            <Icon fontSize="small" />
          </Avatar>
        )}
        <Box>
          {eyebrow && (
            <Typography color="text.secondary" fontSize={12} fontWeight={800} textTransform="uppercase">
              {eyebrow}
            </Typography>
          )}
          <Typography fontSize={{ xs: 20, md: 24 }} fontWeight={900} color="#0e1d2b">
            {title}
          </Typography>
        </Box>
      </Stack>
      {action}
    </Stack>
    {children}
  </Paper>
);

const OptionCard = ({ active, icon: Icon, title, description, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: "100%",
      height: "100%",
      p: 2.5,
      textAlign: "left",
      borderRadius: "22px",
      border: `1px solid ${active ? "#00779f" : "rgba(7, 43, 59, 0.10)"}`,
      background: active
        ? "linear-gradient(135deg, rgba(0,119,159,0.13), rgba(255,255,255,0.94))"
        : "rgba(255,255,255,0.72)",
      boxShadow: active
        ? "0 18px 42px rgba(0, 119, 159, 0.16)"
        : "0 12px 30px rgba(13, 57, 78, 0.08)",
      cursor: "pointer",
      transition: "all 180ms ease",
      display: "flex",
      gap: 2,
      alignItems: "flex-start",
      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: "#00779f",
        boxShadow: "0 18px 42px rgba(0, 119, 159, 0.14)",
      },
    }}
  >
    <Avatar
      sx={{
        mt: 0.25,
        width: 46,
        height: 46,
        color: active ? "#fff" : "#00779f",
        bgcolor: active ? "#00779f" : "rgba(0, 119, 159, 0.10)",
        borderRadius: "16px",
      }}
    >
      <Icon />
    </Avatar>
    <Box flex={1} minWidth={0}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography fontWeight={900} fontSize={18} color="#142234">
          {title}
        </Typography>
        {active && <CheckCircleIcon sx={{ color: "#00779f", fontSize: 20 }} />}
      </Stack>
      <Typography mt={0.75} fontSize={14.5} lineHeight={1.55} color="#64748b">
        {description}
      </Typography>
    </Box>
  </Box>
);

const ProjectCreationStartScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const setupIdFromRoute =
    location.state?.projectSetupId ||
    location.state?.projectSetup?.setupId ||
    searchParams.get("setupId");
  const savedSetup = readProjectSetupFromSession(setupIdFromRoute);
  const routeSetup = location.state?.projectSetup;
  const existingSetup = savedSetup
    ? {
        ...(routeSetup || {}),
        ...savedSetup,
        detailsState: {
          ...(routeSetup?.detailsState || {}),
          ...(savedSetup?.detailsState || {}),
        },
      }
    : routeSetup;
  const projectSetupId = existingSetup?.setupId || setupIdFromRoute || "";
  const shouldReuseExistingProject = Boolean(
    existingSetup?.projectId &&
      (location.state?.resumeExistingProject ||
        searchParams.get("resumeExistingProject") === "1")
  );

  const initialProjectType =
    existingSetup?.projectType ||
    (searchParams.get("type") === "audiobook" ? "audiobook" : "video");
  const mode = searchParams.get("mode");

  const [projectName, setProjectName] = useState(existingSetup?.projectName || "");
  const [projectType, setProjectType] = useState(initialProjectType);
  const [projectOriginalLang, setProjectOriginalLang] = useState(
    existingSetup?.projectOriginalLang || "Original Language"
  );
  const [projectFinalLang, setProjectFinalLang] = useState("Final Languages");
  const [projectFinalLangs, setProjectFinalLangs] = useState(
    existingSetup?.projectFinalLangs || []
  );
  const [voiceEngine, setVoiceEngine] = useState(
    getSupportedVoiceEngine(existingSetup?.voiceEngine)
  );
  const [selectedSourceProject, setSelectedSourceProject] = useState(
    existingSetup?.selectedSourceProject || null
  );
  const [selectedUserObj, setSelectedUserObj] = useState(null);
  const [selectRole, setSelectRole] = useState("select the role of the employee");
  const [displaySelection, setDisplaySelection] = useState(
    existingSetup?.displaySelection || []
  );

  const sortedLanguages = useMemo(
    () => [...languages].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const selectedVoiceEngineLabel = getVoiceEngineLabel(voiceEngine);

  useEffect(() => {
    if (mode !== "embed" || typeof document === "undefined") {
      return;
    }

    const header = document.getElementById("header");
    const footer = document.getElementById("footer");

    if (header) header.style.display = "none";
    if (footer) footer.style.display = "none";
  }, [mode]);

  const handleProjectTypeChange = (newType) => {
    setProjectType(newType);

    if (newType === "audiobook") {
      setProjectOriginalLang("Original Language");
    }
  };

  const handleRemoveLanguage = (code) => {
    setProjectFinalLangs((prev) => prev.filter((languageCode) => languageCode !== code));
  };

  const handleRemoveEmployes = (name) => {
    setDisplaySelection((prev) => prev.filter((item) => item.name !== name));
  };

  const handleProjectSelect = (project) => {
    setSelectedSourceProject(project || null);
  };

  const handleCreate = async () => {
    if (!projectName) {
      toast.error("Project name is required");
      return;
    }

    if (projectType !== "audiobook" && projectOriginalLang === "Original Language") {
      toast.error("Please select the original language");
      return;
    }

    if (projectFinalLangs.length === 0) {
      toast.error("Please select at least one target language");
      return;
    }

    const setupId = projectSetupId || createProjectSetupId();
    let projectId = existingSetup?.projectId || "";

    if (!shouldReuseExistingProject || !projectId) {
      const response = await axios.post(CREATE_PROJECT_NEW, {
        name: projectName,
        employees: displaySelection,
        orginalLang: projectOriginalLang,
        finalLang: projectFinalLangs,
        voiceEngine: getSupportedVoiceEngine(voiceEngine),
      });

      projectId = response.data.projectId;
      console.log(`Project has been added: ${projectId}`);
      toast.success("Project has been added");
    }

    const projectSetup = {
      ...(existingSetup || {}),
      setupId,
      projectId,
      projectName,
      projectType,
      projectOriginalLang,
      projectFinalLangs,
      voiceEngine: getSupportedVoiceEngine(voiceEngine),
      selectedSourceProject,
      displaySelection,
      detailsState: existingSetup?.detailsState || {},
    };

    sessionStorage.setItem(
      getProjectSetupStorageKey(setupId),
      JSON.stringify(projectSetup)
    );

    navigate(
      {
        pathname: "/project-creation-details",
        search: buildProjectCreationSearch({ setupId, mode }),
      },
      {
        state: { projectSetup, projectSetupId: setupId },
      }
    );
  };

  return (
    <Box sx={pageSx}>
      <ToastContainer position="top-center" />

      <Box sx={shellSx}>
        <Stack alignItems="center" textAlign="center" spacing={1.5} mb={4}>
          <Avatar
            sx={{
              width: 58,
              height: 58,
              borderRadius: "18px",
              color: "#00779f",
              bgcolor: "rgba(255,255,255,0.88)",
              boxShadow: "0 16px 45px rgba(0, 119, 159, 0.16)",
            }}
          >
            <AutoAwesomeIcon />
          </Avatar>
          <Chip
            label="Step 1 of 2 · Project setup"
            sx={{
              fontWeight: 800,
              color: "#00779f",
              bgcolor: "rgba(255,255,255,0.78)",
              border: "1px solid rgba(0,119,159,0.12)",
            }}
          />
          <Typography fontSize={{ xs: 34, md: 46 }} fontWeight={950} letterSpacing={-1.5} color="#0d1724">
            Create Project
          </Typography>
          <Typography maxWidth={680} color="#5f7188" fontSize={{ xs: 15, md: 17 }}>
            Start with the essentials, choose the workflow, then continue to upload sources and finish the project.
          </Typography>
        </Stack>

        <Stack spacing={3.5}>
          <Paper elevation={0} sx={{ ...glassCardSx, p: { xs: 2.5, md: 3 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              mb={2.5}
            >
              <Box>
                <Typography fontWeight={900} fontSize={19} color="#142234">
                  Choose project type
                </Typography>
                <Typography color="#64748b" fontSize={14.5}>
                  The next screen changes based on this selection.
                </Typography>
              </Box>
              <Chip
                label={`Active: ${projectType === "video" ? "Video" : "Audiobook"}`}
                sx={{ fontWeight: 900, color: "#00779f", bgcolor: "rgba(0,119,159,0.10)" }}
              />
            </Stack>

            <Grid container spacing={2}>
              {projectTypeOptions.map((option) => (
                <Grid item xs={12} md={6} key={option.value}>
                  <OptionCard
                    active={projectType === option.value}
                    icon={option.icon}
                    title={option.title}
                    description={option.description}
                    onClick={() => handleProjectTypeChange(option.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

          <SectionCard icon={TranslateIcon} title="Project Setup" eyebrow="Required details">
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Typography fontWeight={800} mb={1} color="#243447">
                  Project name
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  value={projectName}
                  placeholder="Enter project name"
                  onChange={(e) => setProjectName(formatInputs(e.target.value))}
                  sx={inputSx}
                />
              </Grid>

              {projectType !== "audiobook" && (
                <Grid item xs={12} md={6}>
                  <Typography fontWeight={800} mb={1} color="#243447">
                    Original language
                  </Typography>
                  <FormControl fullWidth sx={inputSx}>
                    <Select
                      value={projectOriginalLang}
                      onChange={(e) => setProjectOriginalLang(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="Original Language" disabled>
                        Original Language
                      </MenuItem>
                      {sortedLanguages.map((language) => (
                        <MenuItem key={language.code} value={language.code}>
                          {language.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography mt={0.75} fontSize={12.5} color="#00779f" fontWeight={700}>
                    Experimental transcription support
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} md={projectType === "audiobook" ? 6 : 7}>
                <Typography fontWeight={800} mb={1} color="#243447">
                  Target languages
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                  <FormControl sx={{ ...inputSx, minWidth: { xs: "100%", sm: 260 } }}>
                    <Select
                      value={projectFinalLang}
                      onChange={(e) => {
                        if (!projectFinalLangs.includes(e.target.value)) {
                          setProjectFinalLangs([...projectFinalLangs, e.target.value]);
                        }
                        setProjectFinalLang("Final Languages");
                      }}
                    >
                      <MenuItem value="Final Languages" disabled>
                        Add target language
                      </MenuItem>
                      {sortedLanguages.map((language) => (
                        <MenuItem key={language.code} value={language.code}>
                          {language.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {projectFinalLangs.length === 0 ? (
                      <Chip label="No target selected" sx={{ bgcolor: "rgba(100,116,139,0.10)", color: "#64748b" }} />
                    ) : (
                      projectFinalLangs.map((language) => (
                        <Chip
                          key={language}
                          label={getLanguageName(language)}
                          onDelete={() => handleRemoveLanguage(language)}
                          deleteIcon={<CloseIcon />}
                          sx={{
                            height: 38,
                            borderRadius: "14px",
                            fontWeight: 800,
                            color: "#075e7b",
                            bgcolor: "rgba(0, 119, 159, 0.10)",
                            "& .MuiChip-deleteIcon": { color: "#00779f" },
                          }}
                        />
                      ))
                    )}
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                <Typography fontWeight={800} mb={1} color="#243447">
                  Default voice engine
                </Typography>
                <FormControl fullWidth sx={inputSx}>
                  <Select
                    value={voiceEngine}
                    onChange={(e) => setVoiceEngine(e.target.value)}
                    displayEmpty
                  >
                    {VOICE_ENGINE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {projectType === "video" && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1, borderColor: "rgba(7,43,59,0.08)" }} />
                  <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                    <Avatar sx={{ width: 34, height: 34, borderRadius: "12px", bgcolor: "rgba(0,119,159,0.10)", color: "#00779f" }}>
                      <ContentCopyIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={900} color="#142234">
                        Import characters
                      </Typography>
                      <Typography color="#64748b" fontSize={13.5}>
                        Optional: copy characters from an existing project.
                      </Typography>
                    </Box>
                  </Stack>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "20px",
                      background: "rgba(247,250,252,0.74)",
                      border: "1px dashed rgba(0,119,159,0.24)",
                    }}
                  >
                    <ProjectSearch onProjectSelect={handleProjectSelect} />
                    {selectedSourceProject?.name && (
                      <Chip
                        label={`Selected source project: ${selectedSourceProject.name}`}
                        sx={{ mt: 1.5, fontWeight: 800, color: "#00779f", bgcolor: "rgba(0,119,159,0.10)" }}
                      />
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </SectionCard>

          <SectionCard
            icon={GroupsIcon}
            title="Project Members"
            eyebrow="Team access"
            action={(
              <Chip
                label={`${displaySelection.length} member${displaySelection.length === 1 ? "" : "s"}`}
                sx={{ fontWeight: 900, color: "#00779f", bgcolor: "rgba(0,119,159,0.10)" }}
              />
            )}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <UserSelectAutocomplete
                  onUserSelect={(user) => {
                    setSelectedUserObj(user);
                  }}
                  existingUsers={displaySelection}
                  fetchUsersUrl={GET_USERS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth sx={inputSx}>
                  <Select
                    value={selectRole}
                    displayEmpty
                    onChange={(event) => setSelectRole(event.target.value)}
                  >
                    <MenuItem value="select the role of the employee" disabled>
                      Select employee role
                    </MenuItem>
                    {roleOptions.map((role) => (
                      <MenuItem key={role} value={role}>
                        {getRoleOptionLabel(role)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => {
                    if (!selectedUserObj) {
                      toast.error("Please select a valid employee");
                      return;
                    }

                    if (selectRole === "select the role of the employee") {
                      toast.error("Please select a role for the user.");
                      return;
                    }

                    const newItem = {
                      name: selectedUserObj.name,
                      role: selectRole,
                      id: selectedUserObj.id,
                    };

                    setDisplaySelection([...displaySelection, newItem]);
                    setSelectRole("select the role of the employee");
                  }}
                  sx={primaryButtonSx}
                >
                  Add Employee
                </Button>
              </Grid>
            </Grid>

            <Box mt={3}>
              {displaySelection.length === 0 ? (
                <Box
                  sx={{
                    py: 5,
                    borderRadius: "22px",
                    textAlign: "center",
                    border: "1px dashed rgba(7,43,59,0.14)",
                    bgcolor: "rgba(248,250,252,0.72)",
                  }}
                >
                  <GroupsIcon sx={{ color: "#94a3b8", fontSize: 38, mb: 1 }} />
                  <Typography fontWeight={900} color="#334155">
                    No project members added yet
                  </Typography>
                  <Typography color="#64748b" fontSize={14}>
                    Add employees now or leave the project unassigned for later.
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: "22px",
                    border: "1px solid rgba(7,43,59,0.08)",
                    overflow: "hidden",
                    bgcolor: "rgba(255,255,255,0.72)",
                  }}
                >
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "rgba(0,119,159,0.06)" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900, color: "#334155", py: 1.6 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 900, color: "#334155", py: 1.6 }}>Role</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900, color: "#334155", py: 1.6 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displaySelection.map((user, index) => (
                        <TableRow key={`${user.id}-${index}`} hover>
                          <TableCell sx={{ py: 1.8 }}>
                            <Stack direction="row" spacing={1.4} alignItems="center">
                              <Avatar sx={{ width: 34, height: 34, fontSize: 14, bgcolor: alpha("#00779f", 0.12), color: "#00779f", fontWeight: 900 }}>
                                {user.name?.charAt(0)?.toUpperCase() || "U"}
                              </Avatar>
                              <Typography fontWeight={800}>{user.name}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 1.8 }}>
                            <Chip
                              label={getTaskName(user.role)}
                              size="small"
                              sx={{ fontWeight: 800, bgcolor: "rgba(15, 23, 42, 0.06)", color: "#334155" }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.8 }}>
                            <IconButton
                              onClick={() => handleRemoveEmployes(user.name)}
                              sx={{ color: "#ef4444", bgcolor: "rgba(239,68,68,0.08)", "&:hover": { bgcolor: "rgba(239,68,68,0.14)" } }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </SectionCard>

          <Paper
            elevation={0}
            sx={{
              ...glassCardSx,
              p: 2.5,
              position: { md: "sticky" },
              bottom: 18,
              zIndex: 5,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip icon={<MovieCreationIcon />} label={projectType === "video" ? "Video" : "Audiobook"} sx={{ fontWeight: 800 }} />
                <Chip icon={<TranslateIcon />} label={`${projectFinalLangs.length} target language${projectFinalLangs.length === 1 ? "" : "s"}`} sx={{ fontWeight: 800 }} />
                <Chip icon={<GraphicEqIcon />} label={selectedVoiceEngineLabel} sx={{ fontWeight: 800 }} />
              </Stack>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={handleCreate}
                sx={primaryButtonSx}
              >
                {shouldReuseExistingProject ? "Continue" : "Create & Continue"}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
};

export default ProjectCreationStartScreen;
